<script lang="ts">
  import { xhr, toast } from "$app/utils"
  import Button from "$app/components/ButtonSpinner.svelte"
  import Tags from "svelte-tags-input"
  import Honeybadger from "$app/utils/honeybadger"
  import { CirclePlus, ChevronRight } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    categories?: string[]
    values?: string[]
    onadd?: (book: BookType) => void
  }

  let { categories = [], values = [], onadd = () => {} }: Props = $props()

  let errors: Record<string, any> = $state({})

  const initialForm = {
    title: "",
    author: "",
    image: null,
    description: "",
    edition: "",
    publication_date: "",
    categories: [],
    values: []
  }

  let form = $state({ ...initialForm })

  function validForm(): boolean {
    errors = {}

    if (!showImage) {
      errors.image = "Invalid image"
    }
    return Object.keys(errors).length === 0
  }

  const fields = [
    "title",
    "author",
    "description",
    "publication_date",
    "edition",
    "categories",
    "values"
  ]

  function getFormData(): FormData {
    const formData = new FormData()
    fields.forEach((field) => {
      if (["categories", "values"].includes(field)) {
        form[field].forEach((value) => {
          formData.append(`book[${field}][]`, value)
        })
      } else {
        formData.append(`book[${field}]`, form[field] || "")
      }
    })

    if (showImage) {
      formData.append("book[image]", file)
    }
    return formData
  }

  let savingBook = $state(false)
  async function createBook(): Promise<void> {
    if (!validForm()) return

    savingBook = true

    errors = {}

    try {
      const resp = await xhr.post(
        `/plans/${appStore.plan.id}/books.json`,
        getFormData()
      )
      onadd(resp.data)
      toast("The book has been Added.", "success")
    } catch (e: any) {
      toast("There was an error saving the new book", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      savingBook = false
    }
  }

  let input: HTMLInputElement = $state()
  let file: any
  let showImage = $state(false)
  let image: any = $state()

  function onChangeImage(): void {
    file = input.files[0]

    if (file) {
      showImage = true

      const reader = new FileReader()
      reader.addEventListener("load", function () {
        image.setAttribute("src", reader.result)
      })
      reader.readAsDataURL(file)

      return
    }
    showImage = false
  }
</script>

<div class="mt-3 sm:mt-5 overflow-visible w-full">
  <div class="flex w-full justify-center items-center">
    <h3
      class="text-2xl lg:text-3xl text-center leading-6 font-medium text-gray-900"
    >
      Add a Book
    </h3>
  </div>
  <div class="grid grid-cols-12 gap-8 mt-6">
    <div class="col-span-5">
      <div>
        <p class="mb-1">Cover Image</p>
        <div class="input-cont" class:error={errors.thumbnail}>
          {#if showImage}
            <img
              bind:this={image}
              alt="Book Cover"
              id="book_image"
              class="w-auto bg-blue-300 shadow-lg flex-shrink-0"
            />
            <button
              class="text-center relative cursor-pointer bg-white rounded-md font-medium text-black hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
              tabindex="0"
              onclick={() => {
                input.click()
              }}
            >
              <span>Replace Book Cover Image</span>
            </button>
          {:else}
            <button
              onclick={() => {
                input.click()
              }}
              tabindex="0"
              class="w-72 h-96 bg-blue-100 rounded border border-blue-200 flex flex-col items-center justify-center hover:cursor-pointer"
            >
              <CirclePlus class="size-12 text-blue-500" />
              <p class="mt-2 text-xl text-center w-32 text-blue-500">
                Upload Book Cover Image
                <span class="text-base">(333 x 500 px)</span>
              </p>
            </button>
          {/if}
          <div class="text-red-500 text-center pt-4">
            {errors.image || ""}
          </div>
          <div class="text-gray-500 text-center text-sm px-4 mt-2">
            Recommended book cover image size is 333 x 500 px (or a 3:4 aspect
            ratio)
          </div>

          <input
            bind:this={input}
            onchange={onChangeImage}
            type="file"
            accept=".jpg, .jpeg, .png, .webp"
            class="hidden"
          />
        </div>
      </div>
    </div>
    <div class="col-span-7">
      <div class="grid grid-cols-2 gap-2">
        <div class="col-span-2">
          <p class="mb-1">Book Title</p>
          <div class="input-cont" class:error={errors.title}>
            <input
              id="video_title"
              type="text"
              bind:value={form.title}
              class="input"
            />
            <div class="text-red-500">
              {errors.title || ""}
            </div>
          </div>
        </div>
        <div class="col-span-2">
          <p class="mb-1">Author Name</p>
          <div class="input-cont" class:error={errors.author}>
            <input
              id="video_url"
              type="text"
              bind:value={form.author}
              class="input"
            />
            <div class="msg">{errors.author}</div>
          </div>
        </div>

        <div class="col-span-2">
          <p class="mb-1">Categories</p>
          <div class="input-cont" class:error={errors.categories}>
            <div class="svelte-tags">
              <Tags
                bind:tags={form.categories}
                autoComplete={categories}
                addKeys={[]}
                minChars={0}
                removeKeys={[8]}
                onlyUnique={true}
              />
            </div>

            <div class="msg">{errors.categories}</div>
          </div>
        </div>

        <div class="col-span-2">
          <p class="mb-1">Values</p>
          <div class="input-cont" class:error={errors.values}>
            <div class="svelte-tags">
              <Tags
                bind:tags={form.values}
                autoComplete={values}
                minChars={0}
                addKeys={[]}
                removeKeys={[8]}
                onlyUnique={true}
              />
            </div>
          </div>
          <div class="text-red-500">
            {errors.values || ""}
          </div>
        </div>

        <div class="col-span-2">
          <p class="mb-1">Description</p>
          <div class="input-cont" class:error={errors.description}>
            <textarea
              bind:value={form.description}
              rows="3"
              class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
            ></textarea>
            <div class="msg">{errors.description}</div>
          </div>
        </div>

        <div>
          <p class="mb-1">Book Edition</p>
          <div class="input-cont" class:error={errors.edition}>
            <input
              id="video_url"
              type="text"
              bind:value={form.edition}
              class="input"
            />
            <div class="msg">{errors.edition}</div>
          </div>
        </div>

        <div>
          <p class="mb-1">Publication Date</p>
          <input class="input" type="date" bind:value={form.publication_date} />
        </div>

        <div class="col-span-2 mt-6 flex items-center gap-2 justify-center">
          <Button
            class="w-full btn btn-rounded success btn-big"
            processing={savingBook}
            onclick={() => {
              createBook()
            }}
          >
            Next
            <ChevronRight class="size-6" />
          </Button>
        </div>
      </div>
    </div>
  </div>
</div>
